// src/components/map/hooks/adapters/useMapClickSelection.ts
//
// Adapter hook: Click Selection
//
// Owns:
//   - onClick handler (map click → well selection)
//   - selectSingleWell() (single-dashboard auto-select center well)
//   - doCancel() (clear selection, Esc key binding)
//   - dispatchSelectedFeature() (map context dispatch for selected features)
//   - isInSingleDashboard state + capabilities effect
//   - useHotkeys("esc", ...) binding
//   - wellListFilter → doCancel effect
//   - Click event registration effect (split from former combined mouse+click effect)
//
// Mapbox methods called: queryRenderedFeatures, querySourceFeatures
//
// Store reads: selectSelectedWells, setSelectedWells
// Redux reads: filter.wellListFilter, filter.isMidstreamNavigatorFilter
//
// This hook was extracted from MapboxWrapper as part of the concern-based
// decomposition (Phase 4). The combined mouse-event registration effect was
// split so that click registration lives here and hover registration remains
// in MapboxWrapper (moving to useMapHoverLegend in Phase 5).
import { MutableRefObject, useCallback, useEffect, useRef, useState } from "react";
import { useHotkeys } from "react-hotkeys-hook";
import { useDispatch, useSelector } from "react-redux";

import type MapboxDraw from "@mapbox/mapbox-gl-draw";
import {
  LEG_GROUP,
  STICK_LAYER,
  TYPE_LOG_LAYER,
  TYPE_LOG_VERTICAL_LAYER,
  WELL_LABEL_LAYER,
  WELL_LAYER,
  WELL_LAYER_POINT
} from "constants/mapLayers.constants";
import _debounce from "lodash/debounce";
import mapboxgl, { PointLike } from "mapbox-gl";
// eslint-disable-next-line no-restricted-imports -- intentional dual-write for cross-tab redux-state-sync; see EVA-6684
import { setSelectedWells as setSelectedWellsRedux } from "store/features/map/mapSlice";
import { RootState } from "store/rootReducer";
import type { ISelectedWell } from "stores/map";
import { selectSelectedWells, useMapStore } from "stores/map";
import type { WellBounds } from "stores/wellCardPanel";
import { useWellCardPanelStore } from "stores/wellCardPanel";
import { useDebouncedCallback } from "use-debounce";

import type { EntitiesInGroupModel } from "components/map/contexts/MapContextState";
import { claimWellOpen } from "components/map/utils/openSingleWell";

import { DashboardCapabilities } from "../../../../models/dashboard";
import { modes } from "../../utils/drawConstants";
import { emptyFeatureCollection } from "../../utils/emptyFeatureCollection";
import { getSingleWell } from "../../utils/getSingleWell";
import { useMapDispatch } from "../useMapDispatch";

// ── Types ────────────────────────────────────────────────────────────────────

export interface UseMapClickSelectionParams {
  /** Ref to the Mapbox GL map instance. */
  readonly mapboxRef: MutableRefObject<Maybe<mapboxgl.Map>>;
  /** Ref tracking whether the component is still mounted. */
  readonly isMountedRef: MutableRefObject<boolean>;
  /** Map context dispatch function. */
  readonly mapDispatch: ReturnType<typeof useMapDispatch>;
  /** Dashboard capabilities (may be undefined before context loads). */
  readonly capabilities: Maybe<DashboardCapabilities>;
  /** Debounced onMouseMove — cancelled on click to prevent stale hover updates. */
  readonly onMouseMoveDebounce: ReturnType<typeof _debounce>;
  /** Debounced onMouseLeave — cancelled on click to prevent stale leave events. */
  readonly onMouseLeaveDebounce: ReturnType<typeof useDebouncedCallback>;
  /** Ref to the current group → member UWIs mapping. Used to populate
   *  memberUwis on ISelectedWell when clicking a group entity. */
  readonly entitiesInGroupRef: MutableRefObject<Record<string, EntitiesInGroupModel>>;
  /** Ref to the MapboxDraw instance. Used to suppress well selection while a
   *  draw gesture (e.g. drawing an XDA line) is in progress. */
  readonly drawRef: MutableRefObject<Maybe<MapboxDraw>>;
}

interface DoCancelOptions {
  /** Clear the selected wells even when the well-list filter holds them all. */
  readonly clearFilteredSelection?: boolean;
}

export interface UseMapClickSelectionReturn {
  /**
   * Cancel current selection: clears highlighted features, conditionally
   * clears selected wells, and resets the selected-well GeoJSON sources.
   */
  readonly doCancel: (options?: DoCancelOptions) => void;
}

// ── Utilities ────────────────────────────────────────────────────────────────

/**
 * Clear the GeoJSON data from the selected-well layers on the map.
 * Module-level pure function — no React dependencies.
 */
const clearSelectedWellSources = (mapbox: Maybe<mapboxgl.Map>): void => {
  if (!mapbox) return;

  const lineSource = mapbox.getSource("selected-wells") as Maybe<mapboxgl.GeoJSONSource>;
  const pointSource = mapbox.getSource(
    "selected-wells-point"
  ) as Maybe<mapboxgl.GeoJSONSource>;

  lineSource?.setData({ ...emptyFeatureCollection });
  pointSource?.setData({ ...emptyFeatureCollection });
};

/** Grows `bounds` over every position in `coordinates`, at any nesting depth. */
const extendBounds = (
  bounds: Maybe<WellBounds>,
  coordinates: unknown
): Maybe<WellBounds> => {
  if (!Array.isArray(coordinates)) return bounds;
  if (typeof coordinates[0] !== "number") {
    return coordinates.reduce<Maybe<WellBounds>>(extendBounds, bounds);
  }
  const [lng, lat] = coordinates as number[];
  const [[west, south], [east, north]] = bounds ?? [
    [lng, lat],
    [lng, lat]
  ];
  return [
    [Math.min(west, lng), Math.min(south, lat)],
    [Math.max(east, lng), Math.max(north, lat)]
  ];
};

// ── Hook ─────────────────────────────────────────────────────────────────────

export function useMapClickSelection({
  mapboxRef,
  isMountedRef,
  mapDispatch,
  capabilities,
  onMouseMoveDebounce,
  onMouseLeaveDebounce,
  entitiesInGroupRef,
  drawRef
}: UseMapClickSelectionParams): UseMapClickSelectionReturn {
  // ── Store selectors ────────────────────────────────────────────────────
  const selectedWells: Readonly<Record<string, ISelectedWell>> =
    useMapStore(selectSelectedWells);
  const setSelectedWells = useMapStore((s) => s.setSelectedWells);
  const dispatch = useDispatch();

  // ── Redux selectors ────────────────────────────────────────────────────
  const wellListFilter = useSelector((state: RootState) => state.filter.wellListFilter);
  const isMidstreamNavigatorFilter = useSelector(
    (state: RootState) => state.filter.isMidstreamNavigatorFilter
  );

  // ── Local state ────────────────────────────────────────────────────────
  const [isInSingleDashboard, setIsInSingleDashboard] = useState(false);

  // When a draw gesture completes (e.g. finishing an XDA line), mapbox-gl-draw
  // emits `draw.create` and reverts the mode to `simple_select` BEFORE the
  // generic map `click` listener runs — so a getMode() check there is too late
  // and the finalizing click would select whatever well sits under the second
  // point. `draw.create` fires before that click, so we flag it here and have
  // onClick swallow exactly one following click.
  const suppressNextClickRef = useRef(false);

  // ── dispatchSelectedFeature ────────────────────────────────────────────
  const dispatchSelectedFeature = useCallback(
    (features: GeoJSON.Feature[]) => {
      if (isMountedRef.current) {
        mapDispatch({
          payload: {
            selectedFeatures: features
          }
        });
      }
    },
    [isMountedRef, mapDispatch]
  );

  // ── doCancel ───────────────────────────────────────────────────────────
  const doCancel = useCallback(
    ({ clearFilteredSelection }: DoCancelOptions = {}) => {
      // Clear highlighted features via map dispatch.
      // This is equivalent to the former dispatchHighlightedFeatures("", [])
      // which, when called with empty entities, dispatches empty arrays
      // after querying zero features.
      if (isMountedRef.current) {
        mapDispatch({
          payload: {
            highlightedFeatures: { line: [], point: [], facility: [] }
          }
        });
      }

      // Check if selected wells are contained in the well-list filter.
      // If ALL selected wells already appear in the active filter, preserve
      // the selection so the user doesn't lose their filter context.
      const selectedWellUwis = Object.keys(selectedWells);
      let shouldClearSelectedWells = true;

      if (selectedWellUwis.length > 0 && wellListFilter && !clearFilteredSelection) {
        const wellListFilterIndex = wellListFilter.findIndex(
          (filter) => filter !== null && filter.filterType === "well-list"
        );
        if (wellListFilterIndex >= 0) {
          const wellListFilterValues = wellListFilter[wellListFilterIndex]?.value || [];
          const allSelectedWellsInFilter = selectedWellUwis.every((uwi) =>
            wellListFilterValues.includes(uwi)
          );

          if (allSelectedWellsInFilter) {
            shouldClearSelectedWells = false;
          }
        }
      }

      if (shouldClearSelectedWells) {
        setSelectedWells({});
        dispatch(setSelectedWellsRedux({}));
      }

      dispatchSelectedFeature([]);

      clearSelectedWellSources(mapboxRef.current);
    },
    [
      isMountedRef,
      mapDispatch,
      mapboxRef,
      selectedWells,
      wellListFilter,
      setSelectedWells,
      dispatchSelectedFeature,
      dispatch
    ]
  );

  // ── selectSingleWell ──────────────────────────────────────────────────
  const selectSingleWell = useCallback(async () => {
    const map = mapboxRef.current;
    const wells: Record<string, ISelectedWell> = {};
    const singleFeature = await getSingleWell(map);
    if (!singleFeature) {
      return;
    }
    const well = {
      ...singleFeature.properties,
      color: singleFeature?.state?.color
    } as ISelectedWell;
    wells[well.Uwi] = well;

    setSelectedWells(wells);
    dispatch(setSelectedWellsRedux(wells));
    dispatchSelectedFeature([singleFeature]);
  }, [mapboxRef, setSelectedWells, dispatchSelectedFeature, dispatch]);

  // ── Esc key binding ────────────────────────────────────────────────────
  //
  // Not gated on the well card preview: one press closes the popup (its own
  // listener) and clears the selection and highlight it was describing. It
  // takes the card's outline down too. doCancel's other callers leave that
  // outline alone, so only this binding releases it.
  //
  // The selection goes even when the well-list filter holds it. A one-well
  // dashboard would otherwise keep outlining it.
  //
  // A single-well open still in flight is dropped as well, or it selects its
  // well and moves the camera to it after the press.
  useHotkeys("esc", () => {
    claimWellOpen();
    doCancel({ clearFilteredSelection: true });
    useWellCardPanelStore.getState().releaseOutline();
  });

  // ── Capabilities → isInSingleDashboard ─────────────────────────────────
  useEffect(() => {
    if (capabilities) {
      setIsInSingleDashboard(capabilities.hasSingleWellWidget);
    }
  }, [capabilities]);

  // ── isInSingleDashboard → auto-select / cancel ────────────────────────
  useEffect(() => {
    if (isInSingleDashboard) {
      selectSingleWell();
    } else {
      doCancel();
    }
  }, [isInSingleDashboard]); // eslint-disable-line react-hooks/exhaustive-deps -- intentionally keyed only on dashboard mode change

  // ── wellListFilter → cancel selection ──────────────────────────────────
  useEffect(() => {
    if (wellListFilter) {
      const index = wellListFilter.findIndex(
        (filter) => filter !== null && filter.filterType === "well-list"
      );
      if (index >= 0 && !isMidstreamNavigatorFilter) {
        doCancel();
      }
    }
  }, [wellListFilter, isMidstreamNavigatorFilter]); // eslint-disable-line react-hooks/exhaustive-deps -- doCancel is intentionally excluded to avoid re-running on every selection change

  // ── Click event registration ───────────────────────────────────────────
  // Separated from the former combined mousemove/mouseleave/click effect.
  // Hover event registration remains in MapboxWrapper (Phase 5 → useMapHoverLegend).
  useEffect(() => {
    const mapbox = mapboxRef.current;
    if (!mapbox) {
      return;
    }

    const onClick = (e: mapboxgl.MapMouseEvent) => {
      // Cancel any pending hover operations when clicking
      onMouseMoveDebounce.cancel();
      onMouseLeaveDebounce.cancel();

      // Swallow the click that just finalized a draw gesture. `draw.create`
      // (registered below) fires before this listener and sets the flag; by now
      // the draw mode has already reverted to simple_select, so the getMode()
      // check alone cannot catch this case.
      if (suppressNextClickRef.current) {
        suppressNextClickRef.current = false;
        return;
      }

      // Suppress well selection while a draw gesture is active. When finishing
      // an XDA line by clicking on a well, this generic click handler would
      // otherwise select the well and trigger a competing scene render that
      // races the line request. Any mode other than idle select/static means a
      // draw is in progress (two_point_line, polygon, lasso, etc.).
      const drawMode = drawRef.current?.getMode?.();
      if (
        drawMode != null &&
        drawMode !== modes.SIMPLE_SELECT &&
        drawMode !== modes.STATIC
      ) {
        return;
      }

      const map = mapboxRef.current;
      if (!map || !map.getLayer(WELL_LAYER)) {
        return;
      }

      const buffer = 2;
      const bbox = [
        [e.point.x - buffer, e.point.y - buffer],
        [e.point.x + buffer, e.point.y + buffer]
      ] as [PointLike, PointLike];

      const layers: string[] = [WELL_LAYER, WELL_LAYER_POINT, STICK_LAYER];
      if (map.getLayer(TYPE_LOG_LAYER) && map.getLayer(TYPE_LOG_VERTICAL_LAYER)) {
        layers.push(TYPE_LOG_LAYER, TYPE_LOG_VERTICAL_LAYER);
      }

      const features = map.queryRenderedFeatures(bbox, { layers });
      if (features.length === 0 || !features[0]) {
        return;
      }

      const clickedWells: Record<string, ISelectedWell> = {};
      const feat = features[0];
      if (!feat) return;

      const entityUwi: string = feat.properties.Uwi ?? "";
      const groupName: string = feat.properties.group ?? "";
      // A leg is the one `group` that names a real well: the tile emits it as a
      // feature of its parent, carrying the parent's `Uwi` and its own
      // `legUwi`. Clicking one is clicking the parent, so it must not be
      // swept up with the focus bins and sticks, whose ids are synthetic.
      const isSyntheticGroup = !!groupName && groupName !== LEG_GROUP;

      // Resolve member UWIs for group entities from the ref populated by
      // fetchEntitiesOnMap. If the ref hasn't loaded yet the click is a no-op
      // for group expansion — downstream consumers will receive the group
      // entity without memberUwis until the next click after loading completes.
      let memberUwis: readonly string[] | undefined;
      if (isSyntheticGroup) {
        const groupModel = entitiesInGroupRef.current[groupName];
        if (groupModel?.uwis) {
          memberUwis = Object.keys(groupModel.uwis);
        } else {
          // eslint-disable-next-line no-console
          console.warn(
            `[useMapClickSelection] Group entity "${groupName}" clicked but no member UWIs found in entitiesInGroupRef. ` +
              "Member UWIs will be unavailable until entitiesInGroup loads.",
            { entityUwi }
          );
        }
      }

      const baseWell: ISelectedWell = {
        ...feat.properties,
        color: feat?.state?.color,
        ...(memberUwis !== undefined ? { memberUwis } : {})
      } as ISelectedWell;

      clickedWells[entityUwi] = baseWell;

      // Every well this click landed on, for the well card's preview popup to
      // page through. Order is the map's own; duplicates are dropped because a
      // well can be rendered by more than one of the queried layers - which is
      // also what folds a leg into its parent, since both carry the parent's
      // uwi. Focus bins and sticks are left out for the same reason the publish
      // below is skipped for them: their `Uwi` is a synthetic id, not a well.
      const candidates: ISelectedWell[] = [];
      const seenCandidates = new Set<string>();
      const wellBounds: Record<string, WellBounds> = {};
      for (const hit of features) {
        const hitUwi: string = hit?.properties?.Uwi ?? "";
        const hitGroup: string = hit?.properties?.group ?? "";
        const hitIsSynthetic = !!hitGroup && hitGroup !== LEG_GROUP;
        if (!hitUwi || hitIsSynthetic) continue;
        // Every hit, duplicates too: tile clipping splits one well into several,
        // and a leg's hit is part of its parent's extent.
        const geometry: Maybe<GeoJSON.Geometry> = hit.geometry;
        const bounds = extendBounds(
          wellBounds[hitUwi],
          geometry && "coordinates" in geometry ? geometry.coordinates : undefined
        );
        if (bounds) wellBounds[hitUwi] = bounds;
        if (seenCandidates.has(hitUwi)) continue;
        seenCandidates.add(hitUwi);
        candidates.push({
          ...hit.properties,
          color: hit?.state?.color
        } as ISelectedWell);
      }
      // The hits only hold the tile the click landed in, and a well can run on
      // into others, so the popup would sit on the rest of it. Every loaded piece
      // of each candidate grows its box.
      const candidateUwis = Array.from(seenCandidates);
      for (const sourceId of [WELL_LAYER, WELL_LAYER_POINT]) {
        // Querying a source the map has not added yet throws.
        if (candidateUwis.length === 0 || !map.getSource(sourceId)) continue;
        const pieces = map.querySourceFeatures(sourceId, {
          sourceLayer: "eva-wells",
          filter: ["match", ["get", "Uwi"], candidateUwis, true, false]
        });
        for (const piece of pieces) {
          const pieceUwi: string = piece.properties?.Uwi ?? "";
          const geometry: Maybe<GeoJSON.Geometry> = piece.geometry;
          const bounds = extendBounds(
            wellBounds[pieceUwi],
            geometry && "coordinates" in geometry ? geometry.coordinates : undefined
          );
          if (bounds) wellBounds[pieceUwi] = bounds;
        }
      }

      // Enrich selected wells with label data
      const uwis = Array.from(new Set([...Object.keys(clickedWells), ...seenCandidates]));
      const labelFeatures = map.querySourceFeatures(WELL_LABEL_LAYER, {
        sourceLayer: "eva-wells-label",
        filter: ["in", ["get", "Uwi"], ["literal", uwis]]
      });

      const labelDict: Record<string, string> = {};
      for (const label of labelFeatures) {
        if (!labelDict[label.properties.Uwi]) {
          labelDict[label.properties.Uwi] = label.properties.value;
        }
      }
      for (const uwi of uwis) {
        if (!labelDict[uwi]) continue;
        if (clickedWells[uwi]) {
          clickedWells[uwi]["value"] = labelDict[uwi];
        }
      }
      // The paged wells carry their label too: paging re-selects the well it
      // shows, and that write should not be poorer than the click's.
      for (const candidate of candidates) {
        if (labelDict[candidate.Uwi]) {
          (candidate as { value?: string }).value = labelDict[candidate.Uwi];
        }
      }

      setSelectedWells(clickedWells);
      dispatch(setSelectedWellsRedux(clickedWells));

      // Published from inside this handler so the popup inherits its draw-mode
      // swallow, layer list and hit test, and can never describe a different
      // well than the one just selected.
      //
      // Focus bins and sticks are skipped: their `Uwi` is a synthetic group id,
      // not a well, so the card would fetch a well that does not exist.
      // `group` is the same signal the hover path tests (`useWellDetailHover`
      // sets isGroupEntity from it), and sticks carry group === "stick". A leg
      // is the exception, and carries its parent's uwi. Only the X, Escape or
      // another well's click replaces a preview that is up.
      if (entityUwi && !isSyntheticGroup && candidates.length > 0) {
        useWellCardPanelStore.getState().setPreview({
          candidates,
          // The well the click selected is the first hit, so it is also the
          // first candidate: the popup opens on the well the outline is on.
          activeIndex: 0,
          point: { x: e.point.x, y: e.point.y },
          lngLat: e.lngLat,
          wellBounds
        });
      }
    };

    // A completed draw gesture emits `draw.create` immediately before the
    // finalizing `click`. Flag it so onClick swallows that one click and does
    // not select the well sitting under the gesture's last point.
    const onDrawCreate = () => {
      suppressNextClickRef.current = true;
      // Safety net: clear after the current click dispatch so the flag can never
      // leak into a later, genuine selection click if no click follows.
      setTimeout(() => {
        suppressNextClickRef.current = false;
      }, 0);
    };

    mapbox.on("click", onClick);
    mapbox.on("draw.create", onDrawCreate);

    return () => {
      mapbox.off("click", onClick);
      mapbox.off("draw.create", onDrawCreate);
    };
  }, [
    setSelectedWells,
    dispatch,
    mapboxRef?.current, // eslint-disable-line react-hooks/exhaustive-deps -- re-register when map instance changes
    onMouseMoveDebounce,
    onMouseLeaveDebounce,
    drawRef
  ]);

  return { doCancel };
}
